import type { AgentActionButton } from '../types/agent';

export const isAgentFailureMessage = (text: string, actions?: AgentActionButton[]): boolean => {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return false;

  const failurePrefixes = [
    'import failed:',
    'something went wrong:',
    'login failed.',
    'request executed with failure.',
    'function run failed.',
    'test failed.',
    'not authenticated.',
    'action is not allowed:',
    'this action requires explicit confirmation',
    'could not read the values template',
    'import could not add',
    'select a request first',
    'select an environment',
  ];
  if (failurePrefixes.some((prefix) => normalized.startsWith(prefix))) {
    return true;
  }

  if (
    normalized.includes('collection limit exceeded') ||
    normalized.includes('project limit exceeded') ||
    normalized.includes('project limit reached') ||
    normalized.includes('request limit exceeded') ||
    normalized.includes('plan limit exceeded') ||
    normalized.includes('plan limit for') ||
    normalized.includes('environment limit reached') ||
    normalized.includes('custom function limit exceeded') ||
    normalized.includes('custom function limit reached') ||
    normalized.includes('upgrade plan or delete')
  ) {
    return true;
  }

  if (actions?.some((action) => action.label.trim().toLowerCase() === 'try again')) {
    return true;
  }

  const failurePhrases = [
    'selected project was not found',
    'project not found. pick',
    'no project selected.',
    'no environment selected.',
    'project, collection, and request are required',
    'project, collection, and environment are required',
    'environment is required. create/select',
    'project and environment are required for login',
    'already exists in this environment',
    'are not present for',
    'does not have a variable named',
    'does not have these variables',
    'only {{name}} placeholders use environment variables',
    'environment name must be',
    'project name must be',
    'characters or fewer',
    'path parameter(s)',
    'that are not set in the request data',
    'that are not set in the request data table',
    'that are not set in the environment variables',
    'open environment variable and set',
    'missing path parameter',
    'filled from the collections data table',
    'collections data table is for the request body',
    'change {id} to {{id}}',
  ];
  return failurePhrases.some((phrase) => normalized.includes(phrase));
};

export const isAgentSuccessMessage = (text: string, actions?: AgentActionButton[]): boolean => {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return false;

  const successPrefixes = [
    'project "',
    'added environment variable',
    'swagger imported successfully',
    'login executed successfully',
    'request executed successfully',
    'function ran successfully',
    'custom function $',
    'test passed.',
    'collection and request ordering applied',
    'default headers',
    'bearer token applied',
    'saved to environment',
    'login request bodies updated',
    'full flow run completed',
    'environment "',
    'created successfully',
    'imported successfully',
    'executed successfully',
    'applied to all requests',
    'applied to requests',
    'fake data generated',
    'generated fake data',
    'updated request body field',
    'updated request "',
    'environment variables are already applied',
    'environment variable {{',
  ];
  if (successPrefixes.some((prefix) => normalized.startsWith(prefix))) {
    return true;
  }

  if (
    normalized.includes(' created successfully') ||
    normalized.includes(' imported successfully') ||
    normalized.includes(' executed successfully') ||
    (normalized.includes(' applied') && !normalized.includes('not applied')) ||
    normalized.includes('saved to environment') ||
    normalized.includes('to use environment variable placeholders') ||
    (normalized.startsWith('updated ') && normalized.includes('request body field')) ||
    (normalized.startsWith('added ') && normalized.includes('new assertion'))
  ) {
    return true;
  }

  if (
    actions?.some((action) => successActionLabels.has(action.label.trim().toLowerCase())) &&
    !normalized.includes('failed') &&
    !normalized.includes('try again')
  ) {
    return (
      normalized.includes('successfully') ||
      normalized.includes(' applied') ||
      normalized.includes('completed') ||
      normalized.includes('ready') ||
      normalized.startsWith('added environment variable')
    );
  }

  return false;
};

const successActionLabels = new Set([
  'apply suggested ordering',
  'apply default headers',
  'apply bearer',
  'run full flow',
  'open collections',
  'add environment variable',
  'add another variable',
  'create',
]);
